// ProductionProtocolModalButton.js
import React from "react"
import { Button, Modal, Descriptions, Typography, Divider, Segmented, Space } from "antd"
import { FileTextOutlined, DownloadOutlined } from "@ant-design/icons"
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer"
import { computeMaterialVarianceForExport } from "../../../../utils/calcMaterialVariance"
import RobotoRegular from "../../../../fonts/Roboto/Roboto-Regular.ttf" // adjust path
import RobotoMedium from "../../../../fonts/Roboto/Roboto-Medium.ttf"
import RobotoBold from "../../../../fonts/Roboto/Roboto-Bold.ttf"
import RobotoItalic from "../../../../fonts/Roboto/Roboto-Italic.ttf"

const { Text: AntText } = Typography
const STARTUP_M_DEFAULT = 20

// (A) Import font files from src (Vite/CRA both work)

// Register Roboto family with weights/styles you actually have
Font.register({
  family: "Roboto",
  fonts: [
    { src: RobotoRegular, fontWeight: "normal" },
    { src: RobotoMedium, fontWeight: 500 }, // medium
    { src: RobotoBold, fontWeight: "700" }, // bold
    { src: RobotoItalic, fontStyle: "italic" },
  ],
})

// Optional: prevent weird word splits/overlaps
Font.registerHyphenationCallback((word) => [word])

// put these near the top of the file or in a utils module
const humanizeName = (s = "") =>
  s
    .replace(/[_\-.]+/g, " ") // underscores/dots to spaces
    .replace(/(\p{Ll})(\p{Lu})/gu, "$1 $2") // aaaaB -> aaaa B
    .replace(/(\p{Lu})(\p{Lu}\p{Ll})/gu, "$1 $2") // ABCd -> ABC d
    .trim()

const toTitleCase = (s = "") =>
  s.replace(/\p{L}+/gu, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase())

const prettyPerson = (v) => {
  if (!v) return v
  // don't modify emails
  if (/@/.test(v)) return v
  return toTitleCase(humanizeName(v))
}

// ---------- i18n labels ----------
const L = {
  lv: {
    companyHeader: (c) => c || "—",
    title: "RAŽOŠANAS PROTOKOLS Nr.:",
    productName: "Produkta nosaukums",
    orderNo: "Pasūtījuma numurs",
    orderDate: "Pasūtījuma datums",
    orderedQty: "Pasūtītāja daudzums",
    producedQty: "Saražotais daudzums",

    // section 1
    section1: "Ražošanas Iekārta",
    machine: "Ražošanas iekārta",
    materialName: "Materiāla nosaukums",
    planned: "Materiāla plānotais daudzums",
    used: "Izmantotā materiāla fakts",
    manyMaterials: "Drukātāja komentārs",
    scrap: "Brāķis",
    executor: "Izpildītājs",
    started: "Darbs uzsākts",
    finished: "Darbs pabeigts",

    // section 2
    section2: "Pēcpastrādes Iekārta",
    winder: "Winder",
    rollsCount: "Skaits ruļļi",
    rollsWeight: "Ruļļu svars",
    rollsPerRoll: "Ruļļu skaits (uz rulli)",
    rollsInBox: "Ruļļu skaits kastē",
    boxesTotal: "Kopā kastes",
    palletsTotal: "Kopā paletes",
    winderComment: "Winder komentārs",
    movedToWarehouse: "Pārvietots uz noliktavu",
    generatedAtLabel: "Dokuments ģenerēts",

    // footer
    foot: (m) => `* Projekcijas aprēķins ietver ${m} m iekārtas iestartēšanu.`,
    sourceCalc: "aprēķins",
    sourceOrder: "no pasūtījuma",
    fallbackZero: "0 (fallback)",

    button: "Protokols",
    download: "Lejupielādēt PDF",
    close: "Aizvērt",
  },
  en: {
    companyHeader: (c) => c || "—",
    title: "PRODUCTION PROTOCOL No.:",
    productName: "Product name",
    orderNo: "Order number",
    orderDate: "Order date",
    orderedQty: "Ordered quantity",
    producedQty: "Produced quantity",

    // section 1
    section1: "Production Machine",
    machine: "Machine",
    materialName: "Material name",
    planned: "Planned material usage",
    used: "Actual material usage",
    manyMaterials: "Printer Comment",
    scrap: "Scrap",
    executor: "Executor",
    started: "Work started",
    finished: "Work finished",

    // section 2
    section2: "Post-processing Machine",
    winder: "Winder",
    rollsCount: "Rolls count",
    rollsWeight: "Rolls weight",
    rollsPerRoll: "Labels per roll",
    rollsInBox: "Rolls per box",
    boxesTotal: "Total boxes",
    palletsTotal: "Total pallets",
    winderComment: "Winder Comment",
    movedToWarehouse: "Moved to warehouse",
    generatedAtLabel: "Generated",

    // footer
    foot: (m) => `* Projection includes ${m} m of machine startup.`,
    sourceCalc: "calculated",
    sourceOrder: "from order",
    fallbackZero: "0 (fallback)",

    button: "Protocol",
    download: "Download PDF",
    close: "Close",
  },
}

// ---------- PDF styles ----------
const pdfStyles = StyleSheet.create({
  page: { padding: 18, fontSize: 10, fontFamily: "Roboto" },
  headerWrap: { alignItems: "center", marginBottom: 8 },
  headerCompany: { fontSize: 14, fontWeight: 700, marginBottom: 4, textAlign: "center" },
  headerTitle: { textAlign: "center" },
  table: { borderWidth: 1, borderColor: "#000", borderStyle: "solid", marginTop: 10 },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    alignItems: "stretch", // keep cells full height of the row
    minHeight: 0, // gives breathing room
  },
  cellLabel: {
    width: "50%",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  cellValue: {
    width: "50%",
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  text: {
    lineHeight: 1.05, // prevents border from crossing glyphs
    wordBreak: "break-word",
  },
  bold: { fontWeight: 700 },
  sectionTitle: { marginTop: 14, marginBottom: 4, fontWeight: 700 },
  foot: { marginTop: 10, fontSize: 9, opacity: 0.8 },
  generatedAt: { marginTop: 4, fontSize: 9, textAlign: "right", opacity: 0.8 },
})

// ---------- PDF document ----------
function ProtocolPDF({ order, dict, protocolNumber, plannedMeters, usedMeters, generatedAt, winderComment }) {
  const company = order?.manufacturer?.company || order?.client?.manufacturer?.company || "—"
  const productName = order?.selectedProduct?.product?.productName || "—"
  const machine = order?.selectedProduct?.machine || "—"
  const materialName = order?.selectedProduct?.material || "—"
  const fmtGen = (d) =>
    new Intl.DateTimeFormat("lv-LV", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d)

  const fmtTs = (ts) => {
    const s = ts?.seconds
    if (!s && s !== 0) return "—"
    const d = new Date(s * 1000)
    return d.toLocaleString("lv-LV") // keep EU format (matches your screenshot)
  }
  const fmtNum = (n, digits = 0) =>
    n === null || n === undefined || Number.isNaN(Number(n))
      ? "—"
      : Number(n).toLocaleString("lv-LV", {
          maximumFractionDigits: digits,
          minimumFractionDigits: digits,
        })

  const printedBy = prettyPerson(order?.printedByName || order?.selectedProduct.machine) || "—"

  const winderExecutor =
    prettyPerson(order?.rewinderEmployee || order?.rewinderName || order?.rewinderEmail) || "—"

  // prefer the finishing/machine comment, fall back to any user comment
  const printerComment = (order?.machineCompletion?.finishComment || "").toString().trim() || "—"

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.headerWrap}>
          <Text style={pdfStyles.headerCompany}>{dict.companyHeader(company)}</Text>
          <Text style={pdfStyles.headerTitle}>
            {dict.title} {protocolNumber}
          </Text>
        </View>

        {/* Block 1 */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text style={[pdfStyles.text, pdfStyles.bold]}>{dict.productName}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text style={pdfStyles.text} wrap={false}>
                {productName}
              </Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text style={pdfStyles.bold}>{dict.orderNo}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{order?.orderNumber || "—"}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text style={pdfStyles.bold}>{dict.orderDate}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtTs(order?.created)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text style={pdfStyles.bold}>{dict.orderedQty}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.labelCount)} gab.</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text style={pdfStyles.bold}>{dict.producedQty}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.amountCreated)} gab.</Text>
            </View>
          </View>
        </View>

        {/* Section 1 */}
        <Text style={pdfStyles.sectionTitle}>{dict.section1}</Text>
        <View style={pdfStyles.table}>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.machine}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{machine}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.materialName}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{materialName}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.planned}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{plannedMeters != null ? `${fmtNum(plannedMeters)} m` : "—"}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.used}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{usedMeters != null ? `${fmtNum(usedMeters)} m` : "—"}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.manyMaterials}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text style={pdfStyles.text} maxLines={3} ellipsizeMode="tail">
                {printerComment}
              </Text>{" "}
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.scrap}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>—</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.executor}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{printedBy}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.started}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>—</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.finished}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtTs(order?.doneByMachine)}</Text>
            </View>
          </View>
        </View>

        {/* Section 2 */}
        <Text style={pdfStyles.sectionTitle}>{dict.section2}</Text>
        <View style={pdfStyles.table}>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.machine}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{dict.winder}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.executor}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{winderExecutor}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.started}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text> {fmtTs(order?.jobStartedAtWinder) || "-"}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.finished}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtTs(order?.doneByRewinder)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.rollsCount}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.rewinderRollCount)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.rollsWeight}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.rewinderWeight, 2)} kg</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.rollsPerRoll}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.rewinderCountPerRoll)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.rollsInBox}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>—</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.boxesTotal}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtNum(order?.boxesCount)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.palletsTotal}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>—</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.movedToWarehouse}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text>{fmtTs(order?.doneByRewinder)}</Text>
            </View>
          </View>
          <View style={pdfStyles.row}>
            <View style={pdfStyles.cellLabel}>
              <Text>{dict.winderComment}:</Text>
            </View>
            <View style={pdfStyles.cellValue}>
              <Text style={pdfStyles.text} maxLines={3} ellipsizeMode="tail">
                {winderComment}
              </Text>
            </View>
          </View>
        </View>
        <Text style={pdfStyles.generatedAt}>
          {dict.generatedAtLabel}: {fmtGen(generatedAt)}
        </Text>
      </Page>
    </Document>
  )
}

// ---------- Main button+modal ----------
export default function ProductionProtocolModalButton({
  order,
  buttonText = "Protocol",
  buttonProps,
  getProtocolNumber,
}) {
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [mv, setMv] = React.useState(null)
  const [mvErr, setMvErr] = React.useState(null)
  const [lang, setLang] = React.useState("lv") // "lv" | "en"

  const dict = L[lang]

  const fmtTs = (ts) => {
    const s = ts?.seconds
    if (!s && s !== 0) return "—"
    return new Date(s * 1000).toLocaleString("lv-LV", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }
  const fmtNum = (n, digits = 0) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—"
    const val = Number(n)
    return val.toLocaleString("lv-LV", {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    })
  }

  const protocolNumber = React.useMemo(() => {
    if (typeof getProtocolNumber === "function") return getProtocolNumber(order)
    const n = order?.justOrderNumber || order?.id?.slice(-4) || ""
    return `P${String(n)}`
  }, [order, getProtocolNumber])

  const companyName = order?.manufacturer?.company || order?.client?.manufacturer?.company || "—"
  const productName = order?.selectedProduct?.product?.productName || "—"
  const machine = order?.selectedProduct?.machine || "—"
  const materialName = order?.selectedProduct?.material || "—"
  const printerComment = (order?.machineCompletion?.finishComment || "").toString().trim() || "—"
  const winderComment = (order?.rewinderComment || "").toString().trim() || "—"

  const openModal = async () => {
    setOpen(true)
    setLoading(true)
    setMvErr(null)
    try {
      const res = await computeMaterialVarianceForExport(order)
      setMv(res?.kind === "ok" ? res : null)
      if (res?.kind !== "ok") setMvErr(res?.reason || "skip")
    } catch (e) {
      setMv(null)
      setMvErr("error")
    } finally {
      setLoading(false)
    }
  }

  const closeModal = () => setOpen(false)

  const usedMeters = mv?.used ?? order?.machineCompletion?.usedMeters ?? order?.usedMeters ?? null

  if (mvErr) {
    alert(mvErr)
  }

  const plannedMetersInfo = React.useMemo(() => {
    if (mv?.projectedTotal !== undefined) {
      return {
        value: mv.projectedTotal,
        source: "variance",
        startup: mv?.MACHINE_STARTUP_METERS ?? STARTUP_M_DEFAULT,
      }
    }
    const base = Number(order?.projectedMeters)
    if (Number.isFinite(base) && base > 0) {
      return { value: base + STARTUP_M_DEFAULT, source: "order", startup: STARTUP_M_DEFAULT }
    }
    if (Number.isFinite(Number(usedMeters))) {
      return { value: 0, source: "zero", startup: STARTUP_M_DEFAULT }
    }
    return { value: null, source: "none", startup: STARTUP_M_DEFAULT }
  }, [mv, order, usedMeters])

  return (
    <>
      <Button icon={<FileTextOutlined />} onClick={openModal} {...buttonProps}>
        Protocol
      </Button>

      <Modal
        title={
          <div style={{ textAlign: "center", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700 }}>{dict.companyHeader(companyName)}</div>
            <div>
              {dict.title} <AntText code>{protocolNumber}</AntText>
            </div>
          </div>
        }
        open={open}
        onCancel={closeModal}
        footer={
          <div style={{ paddingTop: 30, paddingBottom: 5 }}>
            <Space>
              <Segmented
                options={[
                  { label: "Latviešu", value: "lv" },
                  { label: "English", value: "en" },
                ]}
                value={lang}
                onChange={setLang}
              />
              <PDFDownloadLink
                document={
                  <ProtocolPDF
                    order={order}
                    dict={dict}
                    protocolNumber={protocolNumber}
                    plannedMeters={plannedMetersInfo.value}
                    usedMeters={usedMeters}
                    generatedAt={new Date()}
                    winderComment={winderComment}
                  />
                }
                fileName={`protocol-${protocolNumber}.pdf`}
                style={{ textDecoration: "none" }}>
                {({ loading: gen }) => (
                  <Button icon={<DownloadOutlined />} type="primary" loading={gen}>
                    {dict.download}
                  </Button>
                )}
              </PDFDownloadLink>
              <Button onClick={closeModal}>{dict.close}</Button>
            </Space>
          </div>
        }
        width={820}>
        {/* Header block */}
        <Descriptions
          size="small"
          column={1}
          style={{ marginBottom: 12 }}
          labelStyle={{ width: 260 }}>
          <Descriptions.Item label={dict.productName}>{productName}</Descriptions.Item>
          <Descriptions.Item label={dict.orderNo}>{order?.orderNumber || "—"}</Descriptions.Item>
          <Descriptions.Item label={dict.orderDate}>{fmtTs(order?.created)}</Descriptions.Item>
          <Descriptions.Item label={dict.orderedQty}>
            {fmtNum(order?.labelCount)} gab.
          </Descriptions.Item>
          <Descriptions.Item label={dict.producedQty}>
            {fmtNum(order?.amountCreated)} gab.
          </Descriptions.Item>
        </Descriptions>

        {/* Production */}
        <Divider orientation="left" style={{ marginTop: 16 }}>
          {dict.section1}
        </Divider>
        <Descriptions size="small" column={1} labelStyle={{ width: 260 }}>
          <Descriptions.Item label={dict.machine}>{machine}</Descriptions.Item>
          <Descriptions.Item label={dict.materialName}>{materialName}</Descriptions.Item>

          <Descriptions.Item label={dict.planned}>
            {loading ? (
              "…"
            ) : plannedMetersInfo.value !== null ? (
              <>{fmtNum(plannedMetersInfo.value)} m</>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label={dict.used}>
            {loading ? "…" : usedMeters !== null ? `${fmtNum(usedMeters)} m` : "—"}
          </Descriptions.Item>

          <Descriptions.Item label={dict.manyMaterials}>{printerComment}</Descriptions.Item>
          <Descriptions.Item label={dict.scrap}>—</Descriptions.Item>

          <Descriptions.Item label={dict.executor}>
            {prettyPerson(order?.printedByName || order?.selectedProduct.machine)}
          </Descriptions.Item>
          <Descriptions.Item label={dict.started}>—</Descriptions.Item>
          <Descriptions.Item label={dict.finished} span={2}>
            {fmtTs(order?.doneByMachine)}
          </Descriptions.Item>
        </Descriptions>

        {/* Post-processing */}
        <Divider orientation="left" style={{ marginTop: 16 }}>
          {dict.section2}
        </Divider>
        <Descriptions size="small" column={1} labelStyle={{ width: 260 }}>
          <Descriptions.Item label={dict.machine}>{dict.winder}</Descriptions.Item>
          <Descriptions.Item label={dict.executor}>
            {prettyPerson(order?.rewinderEmployee || order?.rewinderName || order?.rewinderEmail)}
          </Descriptions.Item>

          <Descriptions.Item label={dict.started}>
            {fmtTs(order?.jobStartedAtWinder) || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={dict.finished}>
            {fmtTs(order?.doneByRewinder)}
          </Descriptions.Item>

          <Descriptions.Item label={dict.rollsCount}>
            {fmtNum(order?.rewinderRollCount)}
          </Descriptions.Item>
          <Descriptions.Item label={dict.rollsWeight}>
            {fmtNum((order?.rewinderWeight || 0) / 1000, 2)} kg
          </Descriptions.Item>

          <Descriptions.Item label={dict.rollsPerRoll}>
            {fmtNum(order?.rewinderCountPerRoll)}
          </Descriptions.Item>
          <Descriptions.Item label={dict.rollsInBox}>—</Descriptions.Item>

          <Descriptions.Item label={dict.boxesTotal}>{fmtNum(order?.boxesCount)}</Descriptions.Item>
          <Descriptions.Item label={dict.palletsTotal}>—</Descriptions.Item>

          <Descriptions.Item label={dict.winderComment} span={2}>
            {winderComment}
          </Descriptions.Item>

          <Descriptions.Item label={dict.movedToWarehouse} span={2}>
            {fmtTs(order?.doneByRewinder)}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </>
  )
}
